import { useCallback, useEffect, useReducer, useState } from "react";
import Form from "@src/components/forms/Form";
import { Checkbox, DropDown, FormInput, Loader } from "@src/components/atoms";
import Button from "@src/components/atoms/Button";
import { behaviourSchema, teamSchema } from "../formHelpers";
import FormTextArea from "@src/components/atoms/FormTextArea";
import { dateFormatterUTC } from "@src/constants/convertions";
import { Controller } from "react-hook-form";
import { TiptapEditor } from "@src/components/atoms/RichTextEditor";
import { normalizeEmptyStringsToNull } from "./AdminHelpers";

export const BehaviourForm = ({
  isNew = false,
  data = {},
  onSubmit,
  onCancel,
}) => {
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const initialBvrObject = {
    acronym: data?.acronym || "",
    behaviourType: data?.behaviourType || "",
    name: data?.name || "",
    description: data?.description || "",
    position: data?.position || 0,
    reasons: data?.reasons || "",
    terminology: data?.terminology || "",
    fuelSavingsSource: data?.fuelSavingsSource || "",
    behaviourTypeId: data?.behaviourTypeId || "",
    //more fields
  };

  const positionOptions = [
    { value: 1, text: 1, colour: "purple" },
    { value: 2, text: 2, colour: "blue" },
    { value: 3, text: 3, colour: "orange" },
    { value: 4, text: 4, colour: "pink" },
    { value: 5, text: 5, colour: "teal" },
    { value: 6, text: 6, colour: "green" },
    { value: 7, text: 7, colour: "lightblue" },
    { value: 8, text: 8, colour: "yellow" },
  ];
  const handleSubmit = async (data) => {
    setSubmitting(true);
    setError(null);

    try {
      const cleanedData = normalizeEmptyStringsToNull(data);
      onSubmit && (await onSubmit(cleanedData));
    } catch (err) {
      setError("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isNew && !data?.name) {
    return <Loader />;
  }

  return (
    <div className="flex flex-col overflow-scroll p-4">
      <div className="text-primary-500 font-bold pb-3 uppercase">
        Edit Customer Behaviour
      </div>
      <Form
        onSubmit={(data) => {
          handleSubmit(data);
        }}
        schema={behaviourSchema}
        options={{ defaultValues: initialBvrObject }}
        className="space-y-6"
      >
        {(methods) => {
          const hasChanged = methods.formState.isDirty;

          return (
            <>
              <div className="py-6 space-y-1">
                <span className="text-xs text-gray">Acronym</span>
                <div className="text-xl font-bold">{data?.acronym}</div>
                <span className="text-xs text-gray">BehaviourType</span>
                <div className="text-xl font-bold">{data?.behaviourType}</div>
                <span className="text-xs text-gray">BehaviourTypeId</span>
                <div className="text-xl font-bold">{data?.behaviourTypeId}</div>
                <span className="text-xs text-gray">Name</span>
                <div className="text-xl font-bold">{data?.name}</div>
              </div>
              <div className="py-6 space-y-1">
                <span className="text-xs text-gray">Description</span>
                <div>
                  <FormTextArea
                    className="w-full text-sm border border-neutral-200 h-32"
                    name="description"
                    value={methods.watch("description")}
                    onChange={(e) => {
                      methods.setValue("description", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>
              </div>
              <div className={`rounded`}>
                <span className="text-xs text-gray">Terminology</span>
                <Controller
                  name="terminology"
                  control={methods.control}
                  render={({ field }) => (
                    <TiptapEditor
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="py-6 space-y-1">
                <span className="text-xs text-gray">Reasons</span>
                <div>
                  <FormTextArea
                    className="w-full text-sm border border-neutral-200 h-32"
                    name="reasons"
                    value={methods.watch("reasons")}
                    onChange={(e) => {
                      methods.setValue("reasons", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="py-6 space-y-1">
                <span className="text-xs text-gray">Fuel Savings Source</span>
                <div>
                  <FormTextArea
                    className="w-full text-sm border border-neutral-200 h-32"
                    name="fuelSavingsSource"
                    value={methods.watch("fuelSavingsSource")}
                    onChange={(e) => {
                      methods.setValue("fuelSavingsSource", e.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true,
                      });
                    }}
                  />
                </div>
              </div>
              <div>
                <span className="text-xs text-gray">Position</span>
                <DropDown
                  label="Position"
                  options={positionOptions}
                  value={methods.watch("position")}
                  onChange={(e) => {
                    methods.setValue("position", e, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                />
              </div>
              {data?.createdDate && (
                <div className="py-6 space-y-1">
                  <div className="text-xs text-gray">Created At</div>
                  <div className="font-bold">
                    {dateFormatterUTC(data?.createdDate)}
                  </div>
                </div>
              )}
              <Button submit loading={submitting}>
                Confirm changes
              </Button>
              {error && (
                <div className="mt-4 text-xs italic text-red-500">{error}</div>
              )}
            </>
          );
        }}
      </Form>{" "}
    </div>
  );
};
